import {
	ConnectionProblemException,
	ServiceException,
	UnableToParseServerResponseException
} from '@inter-ikea-kompis/exceptions';
import type { IIowsUser } from '@inter-ikea-kompis/types';

import ServiceFetch from '../../../utilities/ServiceFetch.js';

/**
 * This service handles IOWS authentication.
 */
export default class IowsAuthenticationService {
	private readonly consumer: string;
	private readonly contract: string;
	private readonly endpoint: string;

	/**
	 * Constructor.
	 *
	 * @param options Options.
	 * @param options.consumer Consumer.
	 * @param options.contract Contract.
	 * @param options.endpoint Endpoint.
	 */
	constructor(options: { consumer: string; contract: string; endpoint: string }) {
		this.consumer = options.consumer;
		this.contract = options.contract;
		this.endpoint = options.endpoint;
	}

	/**
	 * Authenticates and returns an IOWS user.
	 *
	 * @returns Promise.
	 */
	public async getUser(): Promise<IIowsUser> {
		let response;
		try {
			response = await window.fetch(this.endpoint, {
				headers: this.getHeaders(),
				credentials: 'include'
			});
		} catch (error) {
			throw new ConnectionProblemException(this.endpoint);
		}

		if (response.status === 419) {
			await this.clearUser();
			return await this.getUser();
		}

		let data;

		try {
			data = await response.json();
		} catch (error) {
			throw new UnableToParseServerResponseException(response);
		}

		if (!data.SessionContext) {
			throw new ServiceException(
				'Failed to fetch user. Response data did not include SessionContext.'
			);
		}

		return {
			id: String(data.SessionContext.UserId.$)
		};
	}

	/**
	 * Clears user session.
	 *
	 * @returns Promise object.
	 */
	private async clearUser(): Promise<void> {
		await ServiceFetch.json(this.endpoint, {
			method: 'DELETE',
			headers: this.getHeaders(),
			credentials: 'include'
		});
	}

	/**
	 * Returns headers.
	 *
	 * @returns Headers.
	 */
	private getHeaders(): HeadersInit {
		return {
			accept: 'application/vnd.ikea.iows+json;version=2',
			'auth-token': this.consumer + '-' + this.contract,
			consumer: this.consumer,
			contract: this.contract
		};
	}
}
